<template>
  <div class="d-flex flex-grow-1 mh-0 flex-column gmb-checkbox-group" :id="id">
    <GmbSearchField
        v-if="searchIsEnabled"
        :searchPlaceholder="searchPlaceholder"
        @search-updated="(searchString) => onSearchUpdated(searchString)"
    />

    <b-button
        v-if="showAllOrNoneButton"
        :class="[
        'd-flex p-2 align-items-center text-size-reset rounded-1',
        { 'mt-2': searchIsEnabled },
      ]"
        variant="light"
        @click="toggleAllOrNone()"
    >
      <FdText tag="span">{{ showAllOrNoneButtonText }}</FdText>
    </b-button>

    <div
        :class="[
        'd-flex flex-grow-1 mh-0 overflow-y-auto flex-column',
        { 'mt-2': searchIsEnabled || showAllOrNoneButton },
      ]"
    >
      <FdScroll y>
        <b-form-checkbox-group
            v-if="allValues"
            v-model="activatedValuesModel"
            :switches="switcherStyle"
        >
          <slot/>
        </b-form-checkbox-group>

        <slot v-else/>
      </FdScroll>
    </div>

    <GmbRow v-if="hasNoDisplayedDomNodeDueToSearch">
      <FdText colorWhite tag="span"
      >Aucun résultat ne correspond aux critères de recherche
      </FdText
      >
    </GmbRow>
  </div>
</template>

<script>
import {convertAsKeywords} from "@/helpers";

import FdScroll from "@/flexdesign/Scroll";
import FdText from "@/flexdesign/Text";

import GmbRow from "@/widgets/Row";
import GmbSearchField from "@/widgets/SearchField";

export default {
  name: "GmbCheckboxGroup",

  components: {
    FdScroll,
    FdText,

    GmbRow,
    GmbSearchField,
  },

  model: {
    event: "changed",
    prop: "activatedValues",
  },

  props: {
    activatedValues: Array,
    allValues: Array,
    disableSearch: Boolean,
    disableSelectAllOrNoneButton: Boolean,
    id: String,
    keywordObjs: Array,
    searchPlaceholder: String,
    switcherStyle: Boolean,
  },

  data() {
    return {
      searchString: "",
      // Fixme why not keep coherence with allValues and activatedValues in naming?
      allDomNodeIds: [],
      displayedDomNodeIds: [],
    };
  },

  computed: {
    activatedValuesModel: {
      get() {
        return this.activatedValues;
      },
      set(newActivatedValues) {
        this.emitActivatedValuesChanged(newActivatedValues);
      },
    },

    hasNoDisplayedDomNodeDueToSearch() {
      return this.searchString && !this.displayedDomNodeIds.length;
    },

    hasSeveralValuesDisplayable() {
      return this.allValues && this.allValues.length >= 2;
    },

    showAllOrNoneButton() {
      return (this.hasSeveralValuesDisplayable &&
          !this.disableSelectAllOrNoneButton &&
          !(this.searchIsEnabled && this.displayedDomNodeIds.length < 2))
    },

    isShowAllButton() {
      let isShowAllButton;

      if (this.searchIsEnabled)
        isShowAllButton = (this.displayedAndActivatedValues.length < this.displayedDomNodeIds.length);
      else
        isShowAllButton = (this.activatedValues.length < this.allValues.length);

      return isShowAllButton
    },

    showAllOrNoneButtonText() {
      return (this.isShowAllButton ? "Tout\xa0sélectionner" : "Tout\xa0désélectionner");
    },

    displayedAndActivatedValues() {
      if (!this.searchIsEnabled)
        return this.activatedValues;

      let displayedAndActivatedValues = [];
      for (let value of this.displayedDomNodeIds) {
        if (this.activatedValues.includes(value)) {
          displayedAndActivatedValues.push(value);
        }
      }
      return displayedAndActivatedValues;
    },

    searchIsEnabled() {
      return this.hasSeveralValuesDisplayable && this.keywordObjs && this.keywordObjs.length && !this.disableSearch;
    },
  },

  watch: {
    activatedValues() {
      if (this.searchIsEnabled)
        this.applySearchOperation();  // Prevent reappearence of hidden checkboxes due to component refresh
    }
  },

  beforeCreate() {
  },

  created() {
    // FIXME this is a bit dangerous because non-reactive here
    if (this.keywordObjs) {
      for (const keywordObj of this.keywordObjs)
        this.allDomNodeIds.push(keywordObj.value);

      this.displayedDomNodeIds = this.allDomNodeIds;
    }
  },

  beforeMount() {
  },

  mounted() {
  },

  beforeUpdate() {
  },

  updated() {
    //console.log("We update Checkboxgroup");
  },

  beforeUnmount() {
  },

  unmounted() {
  },

  methods: {
    emitActivatedValuesChanged(newActivatedValues) {
      this.$emit("changed", newActivatedValues);
    },

    onSearchUpdated(searchString) {
      this.searchString = searchString.trim();
      this.applySearchOperation();
    },

    applySearchOperation() {
      //console.log("We applySearchOperation");
      this._updateDisplayedDomNodeIds();
      // Wait to execute these DOM updates, to not conflict with VueJS DOM updates
      this.$nextTick(this._refreshDomNodeIdsToBeDisplayed, 100);
    },

    _updateDisplayedDomNodeIds() {
      const searchString = this.searchString;

      if (searchString) {
        this.displayedDomNodeIds = [];
        for (const searchKeyword of convertAsKeywords(searchString))
          for (const keywordObj of this.keywordObjs) {
            let toBeDisplayed = false;

            for (const keywordObjKeyword of keywordObj.keywords)
              if (
                  keywordObjKeyword
                      .toLowerCase()
                      .includes(searchKeyword.toLowerCase())
              ) {
                toBeDisplayed = true;  // FIXME wrong algo, it's supposed to be a LOGICAL "AND"
                break;
              }

            if (toBeDisplayed) this.displayedDomNodeIds.push(keywordObj.value);
          }
      } else this.displayedDomNodeIds = [...this.allDomNodeIds];
    },

    _refreshDomNodeIdsToBeDisplayed() {
      //console.log("We _refreshDomNodeIdsToBeDisplayed with displayedDomNodeIds", this.displayedDomNodeIds, "and allDomNodeIds", this.allDomNodeIds);
      let isFirstDisplayedDomNode = true;

      for (const domNodeId of this.allDomNodeIds) {

        const domNode = this.id
            ? document.getElementById(this.id + "-" + domNodeId)
            : document.getElementById(domNodeId);

        if (this.displayedDomNodeIds.includes(domNodeId)) {
          if (domNode.classList.contains("d-none"))
            domNode.classList.remove("d-none");
          domNode.classList.add("d-flex");

          if (isFirstDisplayedDomNode) {
            //console.log("We DISABLE MARGIN ON", domNodeId);
            if (domNode.classList.contains("mt-2")) {
              domNode.classList.remove("mt-2");
            }
            isFirstDisplayedDomNode = false;
          } else {
            //console.log("We ENABLE MARGIN ON", domNodeId);
            domNode.classList.add("mt-2");
          }
        } else {
          if (domNode.classList.contains("d-flex"))
            domNode.classList.remove("d-flex");
          domNode.classList.add("d-none");
        }
      }
    },

    toggleAllOrNone() {
      let newActivatedValues;

      if (!this.searchIsEnabled) {
        if (this.isShowAllButton)
          newActivatedValues = [...this.allValues];
        else
          newActivatedValues = [];
      } else {
        newActivatedValues = [...this.activatedValues];
        for (let value of this.displayedDomNodeIds) {
          if (this.isShowAllButton) {
            if (!this.activatedValues.includes(value))
              newActivatedValues.push(value)
          } else {
            var index = newActivatedValues.indexOf(value);
            if (index !== -1) {
              newActivatedValues.splice(index, 1);
            }
          }
        }
      }

      this.emitActivatedValuesChanged(newActivatedValues);
    },
  },
};
</script>

<style lang="scss"></style>
